import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  Pagination,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchNotifications, setCurrentPage } from '../../redux/slices/notificationsSlice';
import NotificationElement from './NotificationElement';
import { deleteAllYourNotifications, deleteYourNotification, markOnTopNotification } from '../../services/NotificationService';
import { getCurrentData } from '../../utils/utils';
import useAuth from '../../common/hooks/useAuth';

const SEEN_KEY = 'pyj:lastSeenNotification';

export default function NotificationsContainer() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const userId = user?.userId;
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirm, setConfirm] = useState(null); // { type: 'deleteAll' | 'deleteOne' | 'pin', id, pinned }
  const [busy, setBusy] = useState(false);
  const [lastSeen, setLastSeen] = useState(() => {
    try { return Number(localStorage.getItem(SEEN_KEY)) || 0; } catch { return 0; }
  });

  const notifications = useSelector((state) => state.notifications.items) || [];
  const loading = useSelector((state) => state.notifications.loading);
  const totalPages = useSelector((state) => state.notifications.totalPages);
  const currentPage = useSelector((state) => state.notifications.currentPage);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (userId) dispatch(fetchNotifications({ userId, page: currentPage }));
  }, [dispatch, currentPage, userId]);

  const newestId = notifications.reduce((max, n) => Math.max(max, Number(n.notificationId) || 0), 0);
  const unseen = currentPage === 1 && newestId > lastSeen;

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    if (newestId) {
      setLastSeen(newestId);
      try { localStorage.setItem(SEEN_KEY, String(newestId)); } catch { /* ignore */ }
    }
  };

  const refresh = () => dispatch(fetchNotifications({ userId, page: currentPage }));

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.type === 'deleteAll') {
        await deleteAllYourNotifications(userId);
        dispatch(setCurrentPage(1));
      } else if (confirm.type === 'deleteOne') {
        await deleteYourNotification(confirm.id, userId);
      } else if (confirm.type === 'pin') {
        const markOnTop = !confirm.pinned;
        await markOnTopNotification(confirm.id, { markOnTop, markOnTopData: markOnTop ? getCurrentData() : null });
      }
      refresh();
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const confirmTexts = {
    deleteAll: { title: t('jobs.confirmDeletionTitle'), text: t('notifications.confirmDeleteAll'), action: t('common.delete'), color: 'error' },
    deleteOne: { title: t('jobs.confirmDeletionTitle'), text: t('notifications.confirmDeleteOne'), action: t('common.delete'), color: 'error' },
    pinOn: { title: t('notifications.markOnTop'), text: t('notifications.markText'), action: t('notifications.mark'), color: 'primary' },
    pinOff: { title: t('notifications.unmarkOnTop'), text: t('notifications.unmarkText'), action: t('notifications.unmark'), color: 'primary' },
  };
  const dialog = confirm ? confirmTexts[confirm.type === 'pin' ? (confirm.pinned ? 'pinOff' : 'pinOn') : confirm.type] : null;

  return (
    <>
      <Tooltip title={t('notifications.tooltip')}>
        <IconButton
          onClick={handleOpen}
          aria-label={t('notifications.tooltip')}
          aria-controls={open ? 'notifications-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Badge color="secondary" variant="dot" invisible={!unseen} overlap="circular" sx={{ '& .MuiBadge-dot': { width: 10, height: 10, borderRadius: '50%', animation: 'pulseRing 2s infinite' } }}>
            {unseen ? <NotificationsActiveRoundedIcon /> : <NotificationsNoneRoundedIcon />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        id="notifications-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{ sx: { mt: 1, width: 380, maxWidth: 'calc(100vw - 24px)' } }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontWeight: 800 }}>{t('notifications.tooltip')}</Typography>
          <Button size="small" color="error" disabled={notifications.length === 0} onClick={() => { setAnchorEl(null); setConfirm({ type: 'deleteAll' }); }}>
            {t('notifications.deleteAll')}
          </Button>
        </Stack>
        <Divider />
        <Box className="pyj-scroll" sx={{ maxHeight: 'min(360px, 55vh)', overflowY: 'auto' }}>
          {loading && notifications.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ py: 5, px: 3, textAlign: 'center' }}>
              <NotificationsNoneRoundedIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography color="text.secondary" sx={{ mt: 1 }}>{t('notifications.noNotifications')}</Typography>
            </Box>
          ) : (
            notifications.map((notif) => (
              <NotificationElement
                key={notif.notificationId}
                notification={notif}
                handleDeleteSingleNotificationClicked={(id) => { setAnchorEl(null); setConfirm({ type: 'deleteOne', id }); }}
                handleUpsertMarkOnTopClicked={(id, pinned) => { setAnchorEl(null); setConfirm({ type: 'pin', id, pinned }); }}
              />
            ))
          )}
        </Box>
        {totalPages > 1 && (
          <>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.25 }}>
              <Pagination count={totalPages} page={currentPage} onChange={(e, v) => dispatch(setCurrentPage(v))} size="small" color="primary" siblingCount={0} />
            </Box>
          </>
        )}
      </Menu>

      <Dialog open={Boolean(confirm)} onClose={() => !busy && setConfirm(null)} maxWidth="xs" fullWidth>
        {dialog && (
          <>
            <DialogTitle sx={{ fontWeight: 800 }}>{dialog.title}</DialogTitle>
            <DialogContent><DialogContentText>{dialog.text}</DialogContentText></DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setConfirm(null)} disabled={busy} sx={{ color: 'text.secondary' }}>{t('common.cancel')}</Button>
              <Button variant="contained" color={dialog.color} onClick={runConfirm} disabled={busy} sx={dialog.color === 'error' ? { backgroundImage: 'none' } : undefined}>
                {busy ? <CircularProgress size={20} color="inherit" /> : dialog.action}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
